import _ from "lodash";
import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { useHandleScroll } from "Utils/CustomHooks";

import { SearchInput } from "@toddle-edu/ds-web";
import Loading from "UIComponents/Loading";

import classes from "./PaginatedDropdownOverlay.scss";

const defaultImageContainerStyle = {
  width: "100%",
  height: "100%",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
};

const MenuItem = props => {
  const {
    value,
    label,
    subLabel,
    icon,
    backgroundImage,
    imageContainerStyle,
    labelStyle,
    subLabelStyle,
    menuItemContainerStyle,
    onSelectItem,
    isFocused,
    isDisabled,
    onShiftTab,
  } = props;
  const itemRef = useRef();

  useEffect(() => {
    if (isFocused) {
      itemRef.current?.focus();
    }
  }, [isFocused]);

  const onClickItem = () => {
    onSelectItem(value);
  };

  const handleKeyDown = e => {
    if (e.key === "Tab" && e.shiftKey && onShiftTab) {
      e.preventDefault();
      onShiftTab();
    }
  };

  const renderIcon = () => {
    if (!(icon || backgroundImage)) {
      return null;
    }

    return (
      <div className={classes.menuItemIconContainer}>
        {backgroundImage ? (
          <div
            style={{
              backgroundImage,
              ...imageContainerStyle,
            }}
          ></div>
        ) : (
          icon
        )}
      </div>
    );
  };

  return (
    <div
      key={value}
      name={value}
      className={classNames(classes.menuItem, {
        [classes.disabledMenuItem]: isDisabled,
      })}
      style={menuItemContainerStyle}
      onClick={!isDisabled ? onClickItem : _.noop}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      ref={itemRef}
    >
      {renderIcon()}
      <div className={classes.menuItemTextContainer}>
        <div className={classes.menuItemTitle} style={labelStyle}>
          {label}
        </div>
        <div className={classes.menuItemSubText} style={subLabelStyle}>
          {subLabel}
        </div>
      </div>
    </div>
  );
};

const PaginatedDropdownOverlay = props => {
  const {
    isSearchEnabled,
    searchText,
    searchPlaceholder,
    onChangeSearchText,
    options,
    isLoadingFeed,
    isLoadingMoreFeed,
    hasNextPage,
    loadMoreFeed,
    networkStatus,
    imageContainerStyle,
    labelStyle,
    subLabelStyle,
    menuContainerStyle,
    menuItemContainerStyle,
    onSelectItem,
    emptyStateText,
    searchEmptyStateText,
    loadingStyle,
    overlayContainerStyle,
    onClose,
  } = props;

  const overlayListRef = useRef();
  const searchInputRef = useRef();
  const [focusedIndex, setFocusedIndex] = useState(-1);

  useEffect(() => {
    if (isSearchEnabled) searchInputRef.current?.focus();
  }, []);

  const getScrollRef = () => {
    return overlayListRef.current;
  };

  useHandleScroll({
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    getScrollRef,
    // sometimes ref is undefined and pagination doesn't work
    // on first open, passing hookDependencies fixes that
    hookDependencies: [isLoadingFeed, overlayListRef.current],
  });

  const handleSpaceBarClick = e => {
    if (e?.key === " ") {
      e?.preventDefault();
    }
  };

  const handleKeyDown = e => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex(prevIndex =>
          prevIndex < _.size(options) - 1 ? prevIndex + 1 : prevIndex
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex(prevIndex =>
          prevIndex > 0 ? prevIndex - 1 : prevIndex
        );
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (focusedIndex === -1) return;
        onSelectItem(options[focusedIndex].value);
        break;
      case "Tab":
        if (!e.shiftKey) {
          e.preventDefault();
          onClose();
        }
        break;
      default:
        break;
    }
  };

  const handleSearchKeyDown = e => {
    if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault();
      setFocusedIndex(0);
    }
  };

  const handleShiftTab = () => {
    searchInputRef.current.focus();
    setFocusedIndex(-1);
  };

  const renderMenuItemOptions = () => {
    return _.map(options, (option, index) => (
      <MenuItem
        key={option.value}
        value={option.value}
        label={option.label}
        subLabel={option.subLabel}
        icon={option.icon}
        isDisabled={option.isDisabled}
        backgroundImage={option.backgroundImage}
        imageContainerStyle={imageContainerStyle}
        labelStyle={labelStyle}
        subLabelStyle={subLabelStyle}
        menuItemContainerStyle={menuItemContainerStyle}
        onSelectItem={onSelectItem}
        isFocused={index === focusedIndex}
        onShiftTab={handleShiftTab}
      />
    ));
  };

  const renderLoading = () => {
    return (
      <div className={classes.loadingContainer}>
        <Loading {...loadingStyle} />
      </div>
    );
  };

  const renderEmptyState = () => {
    const emptyStateTextClasses = classNames(
      "text-subtitle-2",
      classes.emptyStateText
    );
    const textToDisplay =
      isSearchEnabled && !_.isEmpty(searchText)
        ? searchEmptyStateText
        : emptyStateText;

    return <div className={emptyStateTextClasses}>{textToDisplay}</div>;
  };

  return (
    <div className={classes.overlay} style={overlayContainerStyle}>
      {isSearchEnabled && (
        <div
          className={classes.searchContainer}
          onKeyUp={handleSpaceBarClick}
          onKeyDown={handleSearchKeyDown}
        >
          <SearchInput
            ref={searchInputRef}
            value={searchText}
            placeholder={searchPlaceholder}
            onChange={onChangeSearchText}
          />
        </div>
      )}
      {isLoadingFeed ? (
        renderLoading()
      ) : (
        <div
          className={classes.menuContainer}
          ref={overlayListRef}
          onKeyDown={handleKeyDown}
          style={menuContainerStyle}
        >
          {_.isEmpty(options) ? renderEmptyState() : renderMenuItemOptions()}
          {isLoadingMoreFeed && renderLoading()}
        </div>
      )}
    </div>
  );
};

PaginatedDropdownOverlay.propTypes = {
  isSearchEnabled: PropTypes.bool,
  searchText: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  onChangeSearchText: PropTypes.func,
  options: PropTypes.array,
  isLoadingFeed: PropTypes.bool,
  isLoadingMoreFeed: PropTypes.bool,
  hasNextPage: PropTypes.bool,
  loadMoreFeed: PropTypes.func,
  imageContainerStyle: PropTypes.object,
  labelStyle: PropTypes.object,
  subLabelStyle: PropTypes.object,
  onSelectItem: PropTypes.func,
  emptyStateText: PropTypes.string,
  searchEmptyStateText: PropTypes.string,
  menuContainerStyle: PropTypes.object,
  menuItemContainerStyle: PropTypes.object,
  loadingStyle: PropTypes.object,
  overlayContainerStyle: PropTypes.object,
};

PaginatedDropdownOverlay.defaultProps = {
  isSearchEnabled: false,
  searchText: "",
  searchPlaceholder: "Select",
  onChangeSearchText: () => {},
  options: [],
  isLoadingFeed: false,
  isLoadingMoreFeed: false,
  hasNextPage: false,
  loadMoreFeed: () => {},
  imageContainerStyle: defaultImageContainerStyle,
  labelStyle: {},
  subLabelStyle: {},
  onSelectItem: () => {},
  loadingStyle: {},
  overlayContainerStyle: {},
  menuContainerStyle: {},
  menuItemContainerStyle: {},
};

export default PaginatedDropdownOverlay;
